import type { FC, ReactNode } from 'react';
import { createContext, useMemo } from 'react';
import { useMatch } from 'react-router-dom';

import type {
  AIProviderEnum,
  AnalysisIdOrLatest,
  BrandId,
} from '../api/generated/insights.schemas';
import type { BrandAnalysisData, HostAppProps } from './types';

/**
 * Context for providing brand analysis data throughout the application.
 * Manages the active brand ID, analysis ID, and model for analysis operations.
 */

interface BrandAnalysisDataContextType {
  data: BrandAnalysisData;
}

const BrandAnalysisDataContext = createContext<
  BrandAnalysisDataContextType | undefined
>(undefined);

export { BrandAnalysisDataContext };

interface BrandAnalysisDataProviderProps {
  children: ReactNode;
  brandId?: BrandId;
  analysisId?: AnalysisIdOrLatest;
  model?: AIProviderEnum;
  hostAppProps?: HostAppProps;
}

export const BrandAnalysisDataProvider: FC<BrandAnalysisDataProviderProps> = ({
  children,
  brandId,
  analysisId,
  model,
  hostAppProps = {},
}) => {
  // Use multiple useMatch patterns to catch different route structures
  const matchBrandOnly = useMatch('/brands/:brandId');
  const matchBrandWithWildcard = useMatch('/brands/:brandId/*');
  const matchBrandWithAnalysis = useMatch('/brands/:brandId/analysis/:analysisId');
  const matchBrandWithAnalysisWildcard = useMatch('/brands/:brandId/analysis/:analysisId/*');

  // Extract brandId from whichever pattern matches
  const paramBrandId =
    matchBrandWithAnalysis?.params?.brandId ||
    matchBrandWithAnalysisWildcard?.params?.brandId ||
    matchBrandWithWildcard?.params?.brandId ||
    matchBrandOnly?.params?.brandId;

  const paramAnalysisId =
    matchBrandWithAnalysis?.params?.analysisId ||
    matchBrandWithAnalysisWildcard?.params?.analysisId;

  const data = useMemo<BrandAnalysisData>(
    () => ({
      brandId: paramBrandId || brandId || '',
      analysisId: paramAnalysisId || analysisId || 'latest',
      model: model || 'openai',
      hostAppProps,
    }),
    [paramBrandId, brandId, paramAnalysisId, analysisId, model, hostAppProps],
  );

  const value = useMemo(
    () => ({
      data,
    }),
    [data],
  );

  return (
    <BrandAnalysisDataContext.Provider value={value}>
      {children}
    </BrandAnalysisDataContext.Provider>
  );
};
